import { Controller } from '@hotwired/stimulus'
import { throttle } from 'throttle-debounce'
import { TranscriptionService } from '../transcription/service.js'
import { TranscriptionEvents } from '../transcription/events.js'
import { MicrophoneManager } from '../shared/microphone_manager.js'
import { TtsService } from './tts_service.js'
import { classifyTurn } from '~/voice/turn_detector.js'
import SimpleroPusher from '~/common/helpers/simplero_pusher'
import { AjaxHelper } from '~/common/extensions/ajax_helpers'
import './mode_component.css'

// Module-level (shared by every instance of this controller, since ES modules are
// singletons): a disconnect mid-reply (see disconnect() below) deliberately keeps a
// detached ("zombie") instance's ttsService/Pusher subscription alive so its in-flight
// reply finishes speaking. That zombie has no way to hear a later barge-in — its own
// transcriptionService is already torn down — so without this, tapping the mic again
// while it's still talking would start a second, independent TtsService and the two
// audio timelines could overlap. A freshly-activating controller checks this and
// interrupts whatever zombie reply is still going before starting its own (ENG-34956
// code review). Cleared here or by the zombie's own natural completion in
// #onSpeakingEnded — never left pointing at a ttsService that's already destroyed.
//
// Known gap (ENG-34956 code review, accepted): if the zombie's checkpoint Pusher event
// never arrives (connection drop / stream error mid-reply) and the user never
// reactivates voice mode on that page load, this registration — and the zombie's
// ttsService/Pusher subscription — are never cleared. Narrow (needs an actual mid-stream
// drop, not just normal completion) and no worse than the pre-existing cleanup-ordering
// question any interrupted stream already had; not worth a timeout/pagehide fallback for
// how rarely it fires.
let zombieReply = null

/**
 * Speech mode orchestrator — ChatGPT-style voice conversation loop.
 *
 * State machine: idle → initializing → listening → processing → speaking → listening (loop) | idle
 *
 * UI states:
 * - idle:         Speech icon button (idleButton visible)
 * - initializing: "Cancel" button with spinner (initButton visible)
 * - listening:    "End" button with animated waveform bars (activeButton visible)
 * - processing:   Same as listening (activeButton stays visible)
 * - speaking:     Same as listening (activeButton stays visible, bars animate to TTS)
 */
export default class extends Controller {
  static targets = ['idleButton', 'initButton', 'activeButton', 'waveformBars']

  static values = {
    messagesContainerId: String,
    conversationId: Number,
    turnCheckUrl: String,
    // How long a "real" pause must last before we treat the turn as over.
    settleMs: { type: Number, default: 1200 },
    // Hard backstop: submit a held turn after this long no matter what.
    maxWaitMs: { type: Number, default: 9000 },
    // Abort the borderline LLM gate after this long and just submit.
    gateTimeoutMs: { type: Number, default: 2500 },
  }

  state = 'idle'
  transcriptionService = null
  ttsService = null
  pusherChannel = null
  liveTranscript = null
  audioLevelUnsubscribe = null

  // -- Smart turn state --
  // committed = text from finished segments this turn; live = current segment.
  #committedText = ''
  #liveSegmentText = ''
  #settleTimer = null
  #maxWaitTimer = null
  #gateAbort = null
  // Bumped whenever a pending decision is invalidated (resume / reset / submit),
  // so an in-flight async gate from a stale turn can't fire late.
  #turnGen = 0
  // Set right before this controller's own AjaxHelper.doUpdate call in
  // #submitAndBindPusher, cleared on the next macrotask (setTimeout 0 — guaranteed to run
  // after any microtask-queued Stimulus disconnect() that call triggers, but before any
  // later, independently-triggered disconnect from a subsequent user action). Lets
  // disconnect() below tell "my own submit just replaced my DOM" apart from any OTHER
  // cause of disconnect while mid-reply — e.g. switching conversations in the sidebar,
  // which must still cut the old reply's audio immediately, not preserve it as a zombie
  // (ENG-34956 code review: state === 'speaking' alone can't distinguish the two).
  #ownReplaceInFlight = false

  disconnect() {
    // A Stimulus-lifecycle disconnect (element removed from the document) isn't the same
    // as the user hitting "End" — most commonly it's this very controller's own submit
    // having just replaced its conversation container to promote a brand-new conversation
    // (ENG-34956). replaceWithPreserves()/jQuery replaceWith() is synchronous but Stimulus's
    // disconnect detection (MutationObserver) is async, so #subscribeToPusher already bound
    // the reply's channel and set state 'speaking' before this fires. If we're mid-reply
    // AND this disconnect was caused by that same replace (#ownReplaceInFlight — anything
    // else, e.g. navigating to a different conversation, must not preserve a stale reply),
    // don't cut the assistant off: keep ttsService/the Pusher subscription alive so
    // #handleCheckpoint's own cleanup runs it to completion, same as on a still-connected
    // instance. Explicit "End" (the public deactivate() action below) always tears down
    // fully, including an in-progress reply.
    const keepReplyInFlight =
      this.state === 'speaking' && this.#ownReplaceInFlight

    if (keepReplyInFlight) {
      // Register so a later activate() can interrupt this reply instead of overlapping it.
      const ttsService = this.ttsService
      zombieReply = {
        ttsService,
        stop: () => {
          ttsService?.destroy()
          this.#unbindPusher()
        },
      }
    }

    this.#deactivate({ keepReplyInFlight })
  }

  // -- Public actions --

  activate() {
    if (this.state !== 'idle') return
    this.#doActivate()
  }

  deactivate() {
    this.#deactivate()
  }

  // -- Lifecycle --

  async #doActivate() {
    this.state = 'initializing'
    this.#showInitButton()
    this.#hideSendButton()

    // A prior instance's reply may still be playing (see disconnect()/zombieReply above)
    // — stop it before starting a new session so two replies never overlap.
    if (zombieReply) {
      zombieReply.stop()
      zombieReply = null
    }

    this.ttsService = new TtsService({
      voice: window.UserPreference?.get('preferred_voice') || 'eve',
      playbackRate: 1.0,
    })
    this.ttsService.on('speaking_started', () => this.#onSpeakingStarted())
    this.ttsService.on('speaking_ended', () => this.#onSpeakingEnded())
    this.ttsService.on('error', (e) => console.error('TTS error:', e))

    if (!this.transcriptionService) {
      this.transcriptionService = new TranscriptionService({
        adapter: 'openai',
        vadMode: true,
      })

      this.transcriptionService.on(
        TranscriptionEvents.EVENTS.CONNECTION_ESTABLISHED,
        () => this.#onConnectionEstablished()
      )

      this.transcriptionService.on(
        TranscriptionEvents.EVENTS.TRANSCRIPTION_UPDATE,
        (data) => this.#handleTranscriptionUpdate(data)
      )

      this.transcriptionService.on(
        TranscriptionEvents.EVENTS.TRANSCRIPTION_PROCESSED,
        () => this.#handleTranscriptionProcessed()
      )

      this.transcriptionService.on(
        TranscriptionEvents.EVENTS.TRANSCRIPTION_PROCESSING,
        () => this.#handleSpeechStarted()
      )
    }

    try {
      await this.transcriptionService.start()
    } catch (error) {
      console.error('Speech mode: failed to start transcription', error)
      this.#deactivate()
    }
  }

  #deactivate({ keepReplyInFlight = false } = {}) {
    const wasActive = this.state !== 'idle'
    this.state = 'idle'

    this.#showIdleButton()
    this.#removeLiveTranscript()
    this.#stopWaveformMonitoring()
    this.#stopListening()
    this.transcriptionService = null
    this.#resetTurn()

    if (!keepReplyInFlight) {
      this.ttsService?.destroy()
      this.ttsService = null
      this.#unbindPusher()
    }

    if (wasActive) {
      const container = this.#inputContainer
      container?.classList.remove('js-speech-mode-active')
      this.#restoreSendButton()
    }
  }

  // -- Connection established → transition to listening --

  #onConnectionEstablished() {
    if (this.state !== 'initializing') return

    this.state = 'listening'
    this.#showActiveButton()
    this.#startWaveformMonitoring()

    const container = this.#inputContainer
    container?.classList.add('js-speech-mode-active')
  }

  // -- UI state management --

  #showIdleButton() {
    this.idleButtonTarget.classList.remove('hidden')
    this.initButtonTarget.classList.add('hidden')
    this.activeButtonTarget.classList.add('hidden')
  }

  #showInitButton() {
    this.idleButtonTarget.classList.add('hidden')
    this.initButtonTarget.classList.remove('hidden')
    this.activeButtonTarget.classList.add('hidden')
  }

  #showActiveButton() {
    this.idleButtonTarget.classList.add('hidden')
    this.initButtonTarget.classList.add('hidden')
    this.activeButtonTarget.classList.remove('hidden')
  }

  #hideSendButton() {
    this.#sendButton?.classList.add('hidden')
    this.#dictationButton?.classList.add('hidden')
    // Hide the textarea wrapper so only the footer buttons remain
    this.#textareaWrapper?.classList.add('hidden')
    this.#messageInputWrapper?.classList.add('pt-1')
  }

  #restoreSendButton() {
    this.#textareaWrapper?.classList.remove('hidden')
    this.#messageInputWrapper?.classList.remove('pt-1')
    // Trigger the input handler to restore correct button visibility
    const textarea = this.#findTextarea()
    if (textarea) textarea.dispatchEvent(new Event('input', { bubbles: true }))
    this.#dictationButton?.classList.remove('hidden')
  }

  get #inputContainer() {
    return this.element.closest('.chat-bot-message-input-component')
  }

  get #dictationButton() {
    return this.#inputContainer?.querySelector('.js-chat-bot-dictation-button')
  }

  get #sendButton() {
    return this.#inputContainer?.querySelector('.chat-bot-send-message-button')
  }

  get #textareaWrapper() {
    return this.#inputContainer?.querySelector('.input-auto-grow')
  }

  get #messageInputWrapper() {
    return this.#inputContainer?.querySelector('.js-message-input-wrapper')
  }

  // -- Waveform monitoring --

  #startWaveformMonitoring() {
    this.audioLevelUnsubscribe = MicrophoneManager.onAudioLevel(
      this.#updateWaveformBars
    )
  }

  #stopWaveformMonitoring() {
    if (this.audioLevelUnsubscribe) {
      this.audioLevelUnsubscribe()
      this.audioLevelUnsubscribe = null
    }
    this.#resetWaveformBars()
  }

  #updateWaveformBars = throttle(50, (level) => {
    if (!this.hasWaveformBarsTarget) return
    const bars = this.waveformBarsTarget.querySelectorAll('.voice-speech-bar')
    const scaled = this.#scaleLevel(level)

    bars.forEach((bar, i) => {
      // Stagger bar heights for a natural look
      const offset = [0, 0.3, -0.15][i] || 0
      const height = Math.max(3, Math.min(14, scaled * 14 + offset * 14))
      bar.style.height = `${height}px`
    })
  })

  #resetWaveformBars() {
    if (!this.hasWaveformBarsTarget) return
    const bars = this.waveformBarsTarget.querySelectorAll('.voice-speech-bar')
    bars.forEach((bar) => {
      bar.style.height = '4px'
    })
  }

  #scaleLevel(level) {
    const input = Math.max(level * 100, 0)
    if (input <= 7) return 0
    if (input < 12) return (20 + input) / 100
    if (input < 30) return (30 + input) / 100
    if (input < 95) return (40 + input) / 100
    return 1
  }

  // -- Listening (transcription with VAD) --

  #startListening() {
    this.state = 'listening'
    this.#resetTurn()
  }

  #stopListening() {
    this.transcriptionService?.stop()
  }

  // -- Live transcript in messages container --

  #createLiveTranscript() {
    const container = document.getElementById(this.messagesContainerIdValue)
    if (!container) return

    this.liveTranscript = document.createElement('div')
    this.liveTranscript.className =
      'flex gap-x-2.5 items-start flex-row-reverse'
    this.liveTranscript.innerHTML = `
      <div class="min-w-0">
        <div class="flex justify-end">
          <div class="min-w-0 break-words font-source-serif text-lg italic bg-neutral-300 dark:bg-neutral-800 rounded-xls p-2.5"
               style="max-width: 60%; min-width: min(80%, 100%)">
            <span class="js-live-transcript-text text-fg-alt"></span>
          </div>
        </div>
      </div>
    `
    container.appendChild(this.liveTranscript)
    this.#scrollToBottom(container)
  }

  #updateLiveTranscript(text) {
    if (!this.liveTranscript) this.#createLiveTranscript()

    const span = this.liveTranscript.querySelector('.js-live-transcript-text')
    if (span) span.textContent = text
    this.#scrollToBottom(document.getElementById(this.messagesContainerIdValue))
  }

  #removeLiveTranscript() {
    if (this.liveTranscript) {
      this.liveTranscript.remove()
      this.liveTranscript = null
    }
  }

  #scrollToBottom(container) {
    if (!container) return
    container.scrollTop = container.scrollHeight
  }

  // -- Transcription handlers --

  // The full text of the turn so far: finished segments + current segment.
  get #turnText() {
    return `${this.#committedText} ${this.#liveSegmentText}`
      .replace(/\s+/g, ' ')
      .trim()
  }

  // User started speaking. What that means depends on where we are.
  #handleSpeechStarted() {
    if (this.state === 'speaking') {
      // Barge-in: user spoke over the bot. Stop playback and drop our Pusher
      // handlers (text keeps rendering via message_component_controller), then
      // start a fresh turn — we're listening again even though output streamed.
      this.#unbindPusher()
      this.ttsService?.interrupt()
      this.state = 'listening'
      this.#resetTurn()
    } else if (this.state === 'processing') {
      // User resumed before the reply came back — treat it as the next turn.
      this.state = 'listening'
      this.#resetTurn()
    } else if (this.state === 'listening') {
      // Resumed within the settle window: cancel the pending decision and keep
      // accumulating so everything becomes one combined turn.
      this.#turnGen += 1
      this.#clearTurnTimers()
      this.#cancelGate()
    }
  }

  #handleTranscriptionUpdate(data) {
    if (!data?.text || this.state !== 'listening') return

    this.#liveSegmentText += data.text
    this.#updateLiveTranscript(this.#turnText)
  }

  // A segment finished (a VAD silence boundary). Don't submit yet — fold it into
  // the turn and decide, after a settle pause, whether the user is really done.
  #handleTranscriptionProcessed() {
    if (this.state !== 'listening') return

    this.#committedText = this.#turnText
    this.#liveSegmentText = ''

    const kind = classifyTurn(this.#committedText)

    if (kind === 'empty' || kind === 'filler') {
      // Just thinking out loud ("okay…", "um…") — drop it and keep listening.
      this.#committedText = ''
      this.#removeLiveTranscript()
      this.#clearTurnTimers()
      return
    }

    // Real content: wait a beat for them to continue, then decide.
    this.#armMaxWait()
    this.#armSettle()
  }

  // -- Smart turn decision --

  #armSettle() {
    if (this.#settleTimer) clearTimeout(this.#settleTimer)
    this.#settleTimer = setTimeout(() => {
      this.#settleTimer = null
      this.#onSettle()
    }, this.settleMsValue)
  }

  // Backstop so a held turn is never stranded. Re-armed after each finished
  // segment (a resume clears the timers; the next segment re-arms it), so it
  // bounds how long we sit in silence waiting on the settle window / gate —
  // without ever cutting off a user who is still actively talking.
  #armMaxWait() {
    if (this.#maxWaitTimer) return
    this.#maxWaitTimer = setTimeout(() => {
      this.#maxWaitTimer = null
      if (this.state === 'listening' && this.#committedText.trim() !== '') {
        this.#submitTurn()
      }
    }, this.maxWaitMsValue)
  }

  async #onSettle() {
    if (this.state !== 'listening') return

    const text = this.#committedText
    if (text.trim() === '') return

    if (classifyTurn(text) === 'incomplete') {
      // Trails off mid-thought — ask the server gate before committing.
      const gen = this.#turnGen
      const finished = await this.#checkTurnComplete(text)
      // Bail if the user resumed (or the turn reset/submitted) while we waited.
      if (this.#turnGen !== gen || this.state !== 'listening') return
      if (!finished) return // keep listening; maxWait is the backstop
    }

    this.#submitTurn()
  }

  // Borderline gate. Fails OPEN (returns true) on any error/timeout/abort so a
  // hiccup never strands the user — the stale-turn guard in #onSettle prevents a
  // resume-triggered abort from wrongly submitting.
  async #checkTurnComplete(text) {
    if (!this.turnCheckUrlValue) return true

    this.#cancelGate()
    const controller = new AbortController()
    this.#gateAbort = controller
    const timeout = setTimeout(
      () => controller.abort(),
      this.gateTimeoutMsValue
    )

    try {
      const response = await fetch(this.turnCheckUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute('content'),
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          text,
          conversation_id: this.conversationIdValue,
        }),
        signal: controller.signal,
      })

      if (!response.ok) return true
      const json = await response.json()
      return json.finished !== false
    } catch {
      return true
    } finally {
      clearTimeout(timeout)
      if (this.#gateAbort === controller) this.#gateAbort = null
    }
  }

  #submitTurn() {
    if (this.state !== 'listening') return

    const text = this.#committedText.trim()
    if (text === '') return

    this.#turnGen += 1
    this.#clearTurnTimers()
    this.#cancelGate()

    this.state = 'processing'
    this.#removeLiveTranscript()
    this.#committedText = ''
    this.#liveSegmentText = ''
    this.#submitAndBindPusher(text)
  }

  #resetTurn() {
    this.#turnGen += 1
    this.#committedText = ''
    this.#liveSegmentText = ''
    this.#clearTurnTimers()
    this.#cancelGate()
  }

  #clearTurnTimers() {
    if (this.#settleTimer) {
      clearTimeout(this.#settleTimer)
      this.#settleTimer = null
    }
    if (this.#maxWaitTimer) {
      clearTimeout(this.#maxWaitTimer)
      this.#maxWaitTimer = null
    }
  }

  #cancelGate() {
    this.#gateAbort?.abort()
    this.#gateAbort = null
  }

  // -- Message submission via fetch — binds Pusher from the response directly --

  async #submitAndBindPusher(text) {
    const form = this.element
      .closest('.chat-bot-conversation-component')
      ?.querySelector('form')
    if (!form) return

    const formData = new FormData(form)
    formData.set(`${form.querySelector('[name*="[content]"]')?.name}`, text)
    // Set kind to speech
    const kindField = form.querySelector('.js-message-kind-input')
    if (kindField) formData.set(kindField.name, 'speech')

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          // Without this, a controller action that branches on format (e.g.
          // ChatBot::ConversationsController#create, hit for a brand-new
          // conversation's first turn) resolves to its `format.html` responder
          // instead of `format.json` and comes back as a Turbolinks-redirect JS
          // snippet, not JSON — confirmed live while verifying this fix (ENG-34956).
          Accept: 'application/json',
          'X-CSRF-Token': document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute('content'),
        },
        credentials: 'same-origin',
        body: formData,
      })

      if (!response.ok) {
        if (this.state === 'processing') this.#restartListening()
        return
      }

      const json = await response.json()

      // Apply the response the same way the typed composer does. A brand new
      // conversation's first turn returns `replace` (promotes the unsaved shell to a
      // real conversation) instead of `append` (existing conversation) — handling only
      // `append` here silently dropped that first turn: the draft transcript vanished
      // and nothing was ever saved (ENG-34956). This also disconnects this controller
      // when `replace` swaps out its own DOM subtree, which is fine — cleanup runs via
      // the normal Stimulus disconnect() → #deactivate() lifecycle below. Flagged so
      // disconnect() knows THIS replace (not some unrelated one, e.g. switching
      // conversations) is why it's firing — see #ownReplaceInFlight above.
      this.#ownReplaceInFlight = true
      AjaxHelper.doUpdate(json, form)
      setTimeout(() => {
        this.#ownReplaceInFlight = false
      }, 0)

      // Only bind Pusher for TTS if we're still in processing state.
      // If user barged in during the fetch, state changed to 'listening'
      // and text rendering continues via message_component_controller.
      if (this.state !== 'processing') return

      // Extract Pusher channel name from the assistant message in the raw response
      // (not the live DOM — `replace` above may have just disconnected this controller).
      const temp = document.createElement('div')
      const htmlChunks = [
        ...Object.values(json.append || {}),
        ...Object.values(json.replace || {}),
      ]
      temp.innerHTML = htmlChunks.join('')
      const channelName = temp
        .querySelector('[data-chat-bot--message-assistant-value="true"]')
        ?.getAttribute('data-chat-bot--message-pusher-channel-name-value')

      if (channelName) {
        this.#subscribeToPusher(channelName)
      } else {
        // Fallback: restart listening if no channel found
        this.#restartListening()
      }
    } catch (error) {
      console.error('Speech mode: submit failed', error)
      if (this.state !== 'idle') this.#restartListening()
    }
  }

  // -- Pusher: stream assistant response chunks to TTS --

  #subscribeToPusher(channelName) {
    this.#unbindPusher()
    this.pusherChannel = channelName
    this.state = 'speaking'

    SimpleroPusher.bind(channelName, 'chunk', this.#handleChunk)
    SimpleroPusher.bind(channelName, 'checkpoint', this.#handleCheckpoint)
  }

  #unbindPusher() {
    if (!this.pusherChannel) return
    SimpleroPusher.unbind(this.pusherChannel, 'chunk', this.#handleChunk)
    SimpleroPusher.unbind(
      this.pusherChannel,
      'checkpoint',
      this.#handleCheckpoint
    )
    this.pusherChannel = null
  }

  #handleChunk = (data) => {
    if (data.chunk) this.ttsService?.addText(data.chunk)
  }

  #handleCheckpoint = (data) => {
    if (data.completed) {
      this.ttsService?.flush()
      this.#unbindPusher()
      // TTS speaking_ended callback will restart listening
    }
  }

  // -- TTS callbacks --

  #onSpeakingStarted() {
    // Mic stays active for barge-in detection via VAD
  }

  #onSpeakingEnded() {
    // A zombie's reply finishing naturally — nothing left to interrupt later.
    if (zombieReply?.ttsService === this.ttsService) zombieReply = null

    if (this.state !== 'speaking') return
    this.#restartListening()
  }

  // -- Helpers --

  #restartListening() {
    this.#unbindPusher()
    this.#startListening()
  }

  #findTextarea() {
    return this.#inputContainer?.querySelector('textarea')
  }
}
